import type { RegisterDeviceEventDetail } from '~/@common/types/FCMRegister'

import { webviewService } from './webview.service'

/**
 * 네이티브 앱 ↔ 웹뷰 간 커스텀 이벤트 중앙 관리 버스.
 *
 * 구조:
 *   1. initialize() — 앱 시작 시 모든 이벤트에 대해 window listener 등록 (ready 미전송)
 *   2. subscribe()  — 각 훅/페이지에서 이벤트별 핸들러 등록
 *   3. ready()      — 모든 구독 완료 후 네이티브에게 "준비 완료" 신호
 *
 *   ready() 이후 네이티브가 이벤트를 dispatch하므로,
 *   구독이 모두 끝난 뒤 ready()를 호출하면 이벤트 유실이 없다.
 *
 * 사용법:
 *   // initialize.ts (React 마운트 전, 1회)
 *   nativeEventBus.initialize()
 *
 *   // useApp 등에서 구독
 *   useEffect(() => {
 *     return nativeEventBus.subscribe('onQRCodeReceived', (e) => {
 *       e.detail.worksheetId // ← 타입 안전
 *     })
 *   }, [])
 *
 *   // 모든 앱 레벨 구독 완료 후 (useApp 내부)
 *   nativeEventBus.ready()
 */

// 이벤트별 CustomEvent detail 타입 정의
type NativeEventDetailMap = {
  onQRCodeReceived: { worksheetId?: string; dateTime?: string }
  onVideoIdReceived: { videoId?: string }
  onSignatureBookReceived: { signatureType?: string; curriculumKey?: string }
  onBackPressed: void
  onOrientationLandscape: void
  onOrientationPortrait: void
  onActivated: void
  onDeactivated: void
  registerDevice: RegisterDeviceEventDetail
  navigateTo: { url?: string | null }
}

// 네이티브 앱이 dispatch하는 커스텀 이벤트 목록
const NATIVE_EVENT_NAMES: NativeEventName[] = [
  'onQRCodeReceived', // QR 스캔 → 학습지 채점
  'onVideoIdReceived', // 딥링크 → 강의 상세
  'onSignatureBookReceived', // 딥링크 → 시그니처 강좌 목록
  'onBackPressed', // 안드로이드 뒤로가기
  'onOrientationLandscape', // 가로 전환 → 영상 전체화면
  'onOrientationPortrait', // 세로 전환
  'onActivated', // 앱 포그라운드 복귀
  'onDeactivated', // 앱 백그라운드 전환
  'registerDevice', // FCM 토큰 등록
  'navigateTo', // 푸시 알림 클릭 → 페이지 이동
]

type NativeEventName = keyof NativeEventDetailMap

// detail이 void인 이벤트는 Event, 아닌 이벤트는 CustomEvent<T>
type NativeEvent<N extends NativeEventName> = NativeEventDetailMap[N] extends void
  ? Event
  : CustomEvent<NativeEventDetailMap[N]>

type EventHandler = (event: Event) => void

class NativeEventBus {
  // 이벤트별 구독자 목록
  private handlers = new Map<NativeEventName, Set<EventHandler>>()
  // window에 등록한 dispatcher 레퍼런스 (dispose 시 removeEventListener용)
  private dispatchers = new Map<NativeEventName, EventHandler>()
  // 중복 초기화 방지
  private initialized = false

  /**
   * 앱 초기화 시 1회 호출 (initialize.ts, React 마운트 전).
   * 모든 네이티브 이벤트에 대해 window listener를 등록한다.
   * ready()는 별도로 호출해야 한다.
   */
  initialize() {
    if (this.initialized) return
    this.initialized = true

    NATIVE_EVENT_NAMES.forEach((name) => {
      const dispatcher: EventHandler = (e) => {
        const fns = this.handlers.get(name)
        if (fns?.size) {
          fns.forEach((fn) => fn(e))
        }
      }
      this.dispatchers.set(name, dispatcher)
      window.addEventListener(name, dispatcher)
    })
  }

  /**
   * 등록된 window listener를 모두 제거하고 구독자 맵을 비운다.
   * HMR dispose 등에서 싱글톤 리스너가 중복 누적되는 것을 방지한다.
   */
  dispose() {
    this.dispatchers.forEach((dispatcher, name) => {
      window.removeEventListener(name, dispatcher)
    })
    this.dispatchers.clear()
    this.handlers.clear()
    this.initialized = false
  }

  /**
   * 네이티브에게 웹뷰 준비 완료 신호를 보낸다.
   * 모든 앱 레벨 subscribe가 완료된 후 호출해야 한다.
   */
  ready() {
    webviewService.ready()
  }

  /**
   * 특정 이벤트를 구독한다.
   *
   * @returns unsubscribe 함수 (useEffect cleanup용)
   */
  subscribe<N extends NativeEventName>(
    name: N,
    handler: (event: NativeEvent<N>) => void,
  ): () => void {
    if (!this.handlers.has(name)) {
      this.handlers.set(name, new Set())
    }
    this.handlers.get(name)!.add(handler as EventHandler)

    return () => {
      this.handlers.get(name)?.delete(handler as EventHandler)
    }
  }
}

export const nativeEventBus = new NativeEventBus()
